const FIRST_GROUP = 6;
const SECOND_GROUP = 5;

const digitsOf = (value: string) => value.replace(/\D/g, '');

const toGroups = (personCode: string) => {
  const [first, ...rest] = personCode.split('-');

  return { first: digitsOf(first).slice(0, FIRST_GROUP), second: digitsOf(rest.join('')).slice(0, SECOND_GROUP) };
};

const toPersonCode = (first: string, second: string) =>
  first.length === FIRST_GROUP || second ? `${first}-${second}` : first;

type LvSelection = { start: number; end: number };

const diffRange = (personCode: string, value: string, caret: number) => {
  let prefix = 0;
  while (prefix < personCode.length && prefix < value.length && personCode[prefix] === value[prefix]) prefix++;

  let suffix = 0;
  while (
    suffix < personCode.length - prefix &&
    suffix < value.length - prefix &&
    personCode[personCode.length - 1 - suffix] === value[value.length - 1 - suffix]
  ) {
    suffix++;
  }

  const changed = value.length - suffix - prefix;
  const start = Math.max(0, caret - changed);

  return { start, end: Math.min(personCode.length, start + changed - (value.length - personCode.length)) };
};

const isReplacedRange = (personCode: string, value: string, caret: number, selection: LvSelection) =>
  selection.start >= 0 &&
  selection.start < selection.end &&
  selection.end <= personCode.length &&
  caret >= selection.start &&
  value.slice(0, selection.start) === personCode.slice(0, selection.start) &&
  value.slice(caret) === personCode.slice(selection.end);

export const editLvPersonCode = (personCode: string, value: string, caret: number, selection?: LvSelection) => {
  const { start, end } =
    selection && isReplacedRange(personCode, value, caret, selection)
      ? { start: selection.start, end: selection.end }
      : diffRange(personCode, value, caret);
  const inserted = digitsOf(value.slice(start, caret));

  const hyphen = personCode.indexOf('-');
  const { first, second } = toGroups(personCode);
  const keep = (group: string, from: number) =>
    group.slice(0, Math.max(0, start - from)) + group.slice(Math.max(0, end - from));
  const keptFirst = keep(first, 0);
  const keptSecond = keep(second, hyphen + 1);

  if (!inserted) {
    const deleted = toPersonCode(keptFirst, keptSecond);

    return { personCode: deleted, caret: Math.min(start, deleted.length) };
  }

  const inFirst = hyphen < 0 || start < hyphen || (start === hyphen && keptFirst.length < FIRST_GROUP);
  const room = (inFirst ? FIRST_GROUP - keptFirst.length : 0) + SECOND_GROUP - keptSecond.length;
  const fits = inserted.slice(0, Math.max(0, room));
  const offset = inFirst ? Math.min(start, keptFirst.length) : Math.min(second.length, Math.max(0, start - hyphen - 1));

  const grown = inFirst ? keptFirst.slice(0, offset) + fits + keptFirst.slice(offset) : keptFirst;
  const nextFirst = grown.slice(0, FIRST_GROUP);
  const nextSecond = inFirst
    ? grown.slice(FIRST_GROUP) + keptSecond
    : keptSecond.slice(0, offset) + fits + keptSecond.slice(offset);
  const next = toPersonCode(nextFirst, nextSecond);
  const filled = offset + fits.length;
  const nextCaret = inFirst ? filled + (filled >= FIRST_GROUP ? 1 : 0) : nextFirst.length + 1 + filled;

  return { personCode: next, caret: Math.min(nextCaret, next.length) };
};
